import axiosInstance, { API_BASE_URL } from './axios';
import publicAxios from './axios/public';
import { AxiosResponse } from 'axios';
import { ProductStatus } from '../types/companyUser';
import { PaginatedResponse } from './types';
import {
  ManualPaymentMethod,
  UnreconciledDepositsResponse,
  CreateDepositReconciliationsPayload,
  DepositReconciliation,
  CreateManualPaymentReconciliationPayload,
  ManualPaymentReconciliation,
} from '../types/banking';

export type PaymentMethod = 'bank_transfer' | 'marco_pay' | 'other' | 'cash_control';

export type InvoicingInitialSyncStatus = 'queued' | 'running' | 'completed' | 'failed' | null;

export type InvoicingConnectionState = 'connected' | 'disconnected' | 'error' | 'not_connected';

export interface InvoicingConnectionStatus {
  status: InvoicingConnectionState;
  tokenExpiresAt?: string;
  refreshTokenExpiresAt?: string;
  connectedAt?: string;
  lastSyncAt?: string | null;
  lastError?: string | null;
  lastErrorAt?: string | null;
  initialSyncCompleted?: boolean;
  initialSyncStatus?: InvoicingInitialSyncStatus;
  clientsSyncStatus?: InvoicingInitialSyncStatus;
  productsSyncStatus?: InvoicingInitialSyncStatus;
  clientsSyncedCount?: number;
  productsSyncedCount?: number;
  companyName?: string | null;
  contactGivenName?: string | null;
  contactFamilyName?: string | null;
  contactEmail?: string | null;
  taxId?: string | null;
  realmId?: string | null;
  invoicesSyncErrorCount?: number;
  partnerTaxEnabled?: boolean;
}

export interface InvoicingSyncJob {
  jobId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
}

export interface InvoicingAuthorizeUrl {
  authorizeUrl: string;
}

export interface InvoicingCashControlAccount {
  hasCashControlAccount: boolean;
}

export interface InvoicingRequest {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface InvoiceNoteTemplateSummary {
  id: string;
  name: string;
  text: string;
}

export interface InvoicingClient {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  taxId: string | null;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  addressCountry: string;
  hasBillingAddress: boolean;
  providerCustomerId: string;
  preferredPaymentMethods: PaymentMethod[];
  financedByLender: boolean;
  defaultNoteTemplate: InvoiceNoteTemplateSummary | null;
  lastSyncedAt: string | null;
  syncStatus: 'pending' | 'synced' | 'error' | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InvoicingClientsParams {
  companyId: number;
  page: number;
  pageSize: number;
  search?: string;
  status?: 'active' | 'inactive';
}

export interface InvoicingProduct {
  id: string;
  name: string;
  description: string | null;
  unitPrice: string;
  currency: string;
  unitOfMeasure: string;
  taxable: boolean;
  providerItemId: string;
  lastSyncedAt: string | null;
  syncStatus: 'pending' | 'synced' | 'error' | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InvoicingProductsParams {
  companyId: number;
  page: number;
  pageSize: number;
  search?: string;
  status?: 'active' | 'inactive';
}

export interface InvoicingTerm {
  id: string;
  name: string;
  // Exactly one shape is populated. `dueDays` is a STANDARD term (issue date +
  // N days); `dayOfMonthDue` (+ optional `dueNextMonthDays` rollover window) is
  // a DATE_DRIVEN one. See NewInvoice/dueDateCalculation.ts.
  dueDays: number | null;
  dayOfMonthDue: number | null;
  dueNextMonthDays: number | null;
  providerTermId: string;
  lastSyncedAt: string | null;
  syncStatus: 'pending' | 'synced' | 'error' | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InvoicingTermsParams {
  companyId: number;
  page: number;
  pageSize: number;
  search?: string;
  status?: 'active' | 'inactive';
}

export interface InvoicingTaxCode {
  id: string;
  name: string;
  providerTaxCodeId: string;
  ratePercentage: string | null;
  lastSyncedAt: string | null;
  syncStatus: 'pending' | 'synced' | 'error' | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface InvoicingTaxCodesParams {
  companyId: number;
  page: number;
  pageSize: number;
  search?: string;
  status?: 'active' | 'inactive';
}

export enum ConciliationSource {
  GlobalAccount = 'global_account',
  OutsideMarco = 'outside_marco',
}

export interface InvoiceConciliationDetail {
  id: number;
  source: ConciliationSource;
  depositId: number | null;
  invoiceId: string;
  amount: string;
  reference: string;
  createdAt: string;
  payerName: string | null;
  paymentMethod: ManualPaymentMethod | null;
  paymentDate?: string;
}

export const getInvoicingConnectionStatus = (companyId: number): Promise<InvoicingConnectionStatus> =>
  axiosInstance
    .get(`/companies/${companyId}/products/invoicing/connection/`)
    .then((response: AxiosResponse) => response.data);

export const getInvoicingConnectUrl = (companyId: number): Promise<InvoicingAuthorizeUrl> =>
  axiosInstance
    .get(`/companies/${companyId}/products/invoicing/connect-url/`)
    .then((response: AxiosResponse) => response.data);

export const disconnectInvoicing = (companyId: number): Promise<InvoicingConnectionStatus> =>
  axiosInstance
    .post(`/companies/${companyId}/products/invoicing/connection/disconnect/`)
    .then((response: AxiosResponse) => response.data);

export const getInvoicingRequests = (companyId: number): Promise<InvoicingRequest[]> =>
  axiosInstance
    .get(`/companies/${companyId}/products/invoicing/requests/`)
    .then((response: AxiosResponse) => response.data);

export const toggleInvoicingReadyStatus = (companyId: number): Promise<ProductStatus> =>
  axiosInstance
    .post(`/admins/companies/${companyId}/products/invoicing/toggle_ready_for_invoicing/`)
    .then((response: AxiosResponse) => response.data);

export const getInvoicingClients = ({
  companyId,
  page,
  pageSize,
  search,
  status,
}: InvoicingClientsParams): Promise<PaginatedResponse<InvoicingClient>> =>
  axiosInstance
    .get(`/companies/${companyId}/products/invoicing/clients/`, {
      params: { page, pageSize, search, status },
    })
    .then((response: AxiosResponse) => response.data);

// One client by id. The list endpoint is paginated, so a form that already
// knows its client id (editing an invoice) can't count on that client being in
// the page it loaded — see NewInvoice/index.tsx.
export const getInvoicingClient = (companyId: number, clientId: string): Promise<InvoicingClient> =>
  axiosInstance
    .get(`${invoicingBase(companyId)}/clients/${clientId}/`)
    .then((response: AxiosResponse) => response.data);

export const getInvoicingCashControlAccount = (companyId: number): Promise<InvoicingCashControlAccount> =>
  axiosInstance
    .get(`${invoicingBase(companyId)}/cash-control-account/`)
    .then((response: AxiosResponse) => response.data);

export const setInvoicingClientLenderFinancing = (
  companyId: number,
  clientId: string,
  financedByLender: boolean
): Promise<{ financedByLender: boolean }> =>
  axiosInstance
    .patch(`${invoicingBase(companyId)}/clients/${clientId}/lender-financing/`, {
      financedByLender,
    })
    .then((response: AxiosResponse) => response.data);

export const setInvoicingClientDefaultNoteTemplate = (
  companyId: number,
  clientId: string,
  noteTemplateId: string | null
): Promise<{ noteTemplateId: string | null }> =>
  axiosInstance
    .patch(`${invoicingBase(companyId)}/clients/${clientId}/default-note-template/`, {
      noteTemplateId,
    })
    .then((response: AxiosResponse) => response.data);

export const getInvoicingProducts = ({
  companyId,
  page,
  pageSize,
  search,
  status,
}: InvoicingProductsParams): Promise<PaginatedResponse<InvoicingProduct>> =>
  axiosInstance
    .get(`/companies/${companyId}/products/invoicing/products/`, {
      params: { page, pageSize, search, status },
    })
    .then((response: AxiosResponse) => response.data);

export const getInvoicingTerms = ({
  companyId,
  page,
  pageSize,
  search,
  status,
}: InvoicingTermsParams): Promise<PaginatedResponse<InvoicingTerm>> =>
  axiosInstance
    .get(`/companies/${companyId}/products/invoicing/terms/`, {
      params: { page, pageSize, search, status },
    })
    .then((response: AxiosResponse) => response.data);

export const getInvoicingTaxCodes = ({
  companyId,
  page,
  pageSize,
  search,
  status,
}: InvoicingTaxCodesParams): Promise<PaginatedResponse<InvoicingTaxCode>> =>
  axiosInstance
    .get(`/companies/${companyId}/products/invoicing/tax-codes/`, {
      params: { page, pageSize, search, status },
    })
    .then((response: AxiosResponse) => response.data);

export const triggerInvoicingInitialSync = (companyId: number): Promise<InvoicingSyncJob> =>
  axiosInstance
    .post(`/companies/${companyId}/products/invoicing/initial-sync/`)
    .then((response: AxiosResponse) => response.data);

export const triggerInvoicingClientsSync = (companyId: number): Promise<InvoicingSyncJob> =>
  axiosInstance
    .post(`/companies/${companyId}/products/invoicing/clients/sync/`)
    .then((response: AxiosResponse) => response.data);

export const triggerInvoicingProductsSync = (companyId: number): Promise<InvoicingSyncJob> =>
  axiosInstance
    .post(`/companies/${companyId}/products/invoicing/products/sync/`)
    .then((response: AxiosResponse) => response.data);

export const triggerInvoicingTaxCodesSync = (companyId: number): Promise<InvoicingSyncJob> =>
  axiosInstance
    .post(`/companies/${companyId}/products/invoicing/tax-codes/sync/`)
    .then((response: AxiosResponse) => response.data);

export interface InvoicingUserInfo {
  id: number;
  email: string;
  name: string;
}

export interface InvoicingAdminConnectionInfo {
  connection: InvoicingConnectionStatus;
  initiatedBy?: InvoicingUserInfo | null;
}

export const getInvoicingAdminConnectionInfo = (companyId: number): Promise<InvoicingAdminConnectionInfo> =>
  axiosInstance
    .get(`/admins/companies/${companyId}/products/invoicing/connection/`)
    .then((response: AxiosResponse) => response.data);

// Re-reads the cached QuickBooks profile fields (company name, connected user,
// tax id) from QBO. Profile only — this does not start a data re-sync.
export const refreshInvoicingConnectionProfile = (companyId: number): Promise<InvoicingAdminConnectionInfo> =>
  axiosInstance
    .post(`/admins/companies/${companyId}/products/invoicing/connection/refresh/`)
    .then((response: AxiosResponse) => response.data);

export type InvoiceStatus = 'DRAFT' | 'SENT' | 'PARTIALLY_PAID' | 'OVERDUE' | 'PAID' | 'VOIDED';
export type InvoiceSyncStatus = 'pending' | 'synced' | 'error' | null;
export type RecipientStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface Invoice {
  id: string;
  number: string;
  buyer: string;
  client: string;
  issueDate: string | null;
  dueDate: string;
  salesTerm: string | null;
  taxCode: string | null;
  amount: string;
  taxAmount: string | null;
  pendingConciliationAmount: string;
  currency: string;
  paymentMethods: PaymentMethod[];
  notes: string | null;
  noteTemplate: string | null;
  status: InvoiceStatus;
  recipientStatus: RecipientStatus;
  providerInvoiceId: string | null;
  syncStatus: InvoiceSyncStatus;
  lastSyncError: string | null;
  paymentLink: string;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceLineItem {
  id: string;
  product: string;
  productDescription: string;
  productQuantity: number;
  productUnitPrice: string;
  totalLineAmount: string;
  isTaxable: boolean;
}

export interface InvoiceTimelineEvent {
  id: string;
  type: string;
  date: string;
  amount: string | null;
  detail: string;
  note: string | null;
}

export interface InvoiceDetail extends Invoice {
  details: InvoiceLineItem[];
  timeline: InvoiceTimelineEvent[];
  conciliationDetails: InvoiceConciliationDetail[];
  // Status-derived action policy, computed by the invoicing service so the rule
  // lives in one place instead of being re-derived by every client.
  canEdit: boolean;
  canSend: boolean;
  canDownload: boolean;
  clientEmail: string;
}

// The anonymous recipient's view of an invoice, served by
// GET /api/v2/products/invoicing/invoices/<token>/ — a deliberately narrower
// shape than InvoiceDetail, which is company-scoped and carries provider
// internals. Decimal fields arrive as strings, as everywhere else in this module.
export interface PublicInvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: string;
}

export interface PublicInvoiceBuyer {
  name: string;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
}

export interface PublicInvoiceCompany {
  name: string | null;
  logo: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
}

export interface PublicInvoiceRoutingCode {
  // Airwallex routing-code type: 'ach', 'fedwire', 'swift', 'bic'...
  type: string;
  value: string;
}

export interface PublicInvoiceBankTransfer {
  bankName: string;
  accountName: string;
  // Full and unmasked, unlike the Add Funds summary: the payer needs it to
  // actually send the transfer.
  accountNumber: string;
  routingCodes: PublicInvoiceRoutingCode[];
}

export interface PublicInvoiceMarcoPay {
  businessName: string;
  marcoPayNumber: string;
}

export interface PublicInvoice {
  number: string | null;
  issueDate: string | null;
  dueDate: string;
  currency: string;
  paymentMethods: PaymentMethod[];
  status: InvoiceStatus;
  recipientStatus: RecipientStatus;
  canEdit: boolean;
  amount: string;
  taxAmount: string | null;
  balanceDue: string | null;
  buyer: PublicInvoiceBuyer;
  company: PublicInvoiceCompany;
  lineItems: PublicInvoiceLineItem[];
  notes: string | null;
  bankTransfer?: PublicInvoiceBankTransfer;
  marcoPay?: PublicInvoiceMarcoPay;
}

export interface InvoiceWriteLineItem {
  product: string;
  productDescription: string;
  productQuantity: number;
  productUnitPrice: string;
  position: number;
  isTaxable: boolean;
}

export interface InvoiceWritePayload {
  client: string;
  issueDate: string | null;
  dueDate: string;
  salesTerm: string | null;
  taxCode: string | null;
  currency: string;
  // At least one entry, always — the backend rejects an empty list.
  paymentMethods: PaymentMethod[];
  notes?: string;
  noteTemplate?: string | null;
  items: InvoiceWriteLineItem[];
  // When true the invoice is persisted as SENT and pushed to the provider once,
  // in the same request, which also kicks off the PDF and the client email.
  send?: boolean;
}

export interface InvoicesFilters {
  page?: number;
  pageSize?: number;
  number?: string;
  buyer?: string;
  status?: InvoiceStatus;
  dueDate?: string;
  startDate?: string;
  endDate?: string;
}

export interface InvoiceSettings {
  companyName: string | null;
  companyAddressLine1: string | null;
  companyAddressLine2: string | null;
  replyToEmail: string | null;
  logo: string | null;
  invoicePrefix: string | null;
  nextInvoiceNumber: number | null;
  defaultSalesTerm: string | null;
  defaultDueDateIsCustom: boolean;
  defaultCurrency: string | null;
  qboCustomTxnNumbersEnabled: boolean | null;
}

export interface InvoiceNoteTemplate {
  id: string;
  name: string;
  text: string;
  updatedAt: string;
}

const invoicingBase = (companyId: number) => `/companies/${companyId}/products/invoicing`;

export const getInvoices = (
  companyId: number,
  filters: InvoicesFilters = {}
): Promise<PaginatedResponse<Invoice>> =>
  axiosInstance
    .get(`${invoicingBase(companyId)}/invoices/`, { params: filters })
    .then((response: AxiosResponse) => response.data);

export const getInvoiceDetail = (companyId: number, invoiceId: string): Promise<InvoiceDetail> =>
  axiosInstance
    .get(`${invoicingBase(companyId)}/invoices/${invoiceId}/`)
    .then((response: AxiosResponse) => response.data);

// Two outcomes, told apart by the status code rather than the body: 200 carries
// the PDF, 202 means it is being re-rendered and will be emailed. `.then` keeps
// the status, which the default `response.data` unwrapping used by the calls
// around this one would throw away.
export const downloadInvoicePdf = (
  companyId: number,
  invoiceId: string
): Promise<{ data: Blob; status: number }> =>
  axiosInstance
    .get(`${invoicingBase(companyId)}/invoices/${invoiceId}/pdf/`, { responseType: 'blob' })
    .then(({ data, status }) => ({ data, status }));

// No company id and no auth header: the signed token in the path is the whole
// credential. Not encoded — Signer output is "<uuid>:<urlsafe-b64>", all
// path-safe, and the emailed link carries it raw.
export const getPublicInvoice = (token: string): Promise<PublicInvoice> =>
  publicAxios.get(`/products/invoicing/invoices/${token}/`).then((response: AxiosResponse) => response.data);

export const approvePublicInvoice = (token: string): Promise<void> =>
  publicAxios.post(`/products/invoicing/invoices/${token}/approve/`).then(() => undefined);

export const rejectPublicInvoice = (token: string, note: string): Promise<void> =>
  publicAxios.post(`/products/invoicing/invoices/${token}/reject/`, { note }).then(() => undefined);

export const getPublicInvoicePdfUrl = (token: string): string =>
  `${API_BASE_URL}/products/invoicing/invoices/${token}/pdf/`;

export const createInvoice = (companyId: number, payload: InvoiceWritePayload): Promise<Invoice> =>
  axiosInstance
    .post(`${invoicingBase(companyId)}/invoices/`, payload)
    .then((response: AxiosResponse) => response.data);

export const updateInvoice = (
  companyId: number,
  invoiceId: string,
  payload: Partial<InvoiceWritePayload>
): Promise<Invoice> =>
  axiosInstance
    .patch(`${invoicingBase(companyId)}/invoices/${invoiceId}/`, payload)
    .then((response: AxiosResponse) => response.data);

export const voidInvoice = (companyId: number, invoiceId: string): Promise<Invoice> =>
  axiosInstance
    .post(`${invoicingBase(companyId)}/invoices/${invoiceId}/void/`)
    .then((response: AxiosResponse) => response.data);

export const retryInvoiceSync = (companyId: number, invoiceId: string): Promise<Invoice> =>
  axiosInstance
    .post(`${invoicingBase(companyId)}/invoices/${invoiceId}/retry-sync/`)
    .then((response: AxiosResponse) => response.data);

export const getInvoiceSettings = (companyId: number): Promise<InvoiceSettings> =>
  axiosInstance
    .get(`${invoicingBase(companyId)}/invoices/settings/`)
    .then((response: AxiosResponse) => response.data);

export const updateInvoiceSettings = (
  companyId: number,
  payload: Partial<InvoiceSettings> | FormData
): Promise<InvoiceSettings> =>
  axiosInstance
    .patch(`${invoicingBase(companyId)}/invoices/settings/`, payload)
    .then((response: AxiosResponse) => response.data);

export const getInvoiceNoteTemplates = (companyId: number): Promise<PaginatedResponse<InvoiceNoteTemplate>> =>
  axiosInstance
    .get(`${invoicingBase(companyId)}/invoices/note-templates/`, { params: { pageSize: 100 } })
    .then((response: AxiosResponse) => response.data);

export const createInvoiceNoteTemplate = (
  companyId: number,
  name: string,
  text: string
): Promise<InvoiceNoteTemplate> =>
  axiosInstance
    .post(`${invoicingBase(companyId)}/invoices/note-templates/`, { name, text })
    .then((response: AxiosResponse) => response.data);

export const updateInvoiceNoteTemplate = (
  companyId: number,
  templateId: string,
  text: string
): Promise<InvoiceNoteTemplate> =>
  axiosInstance
    .patch(`${invoicingBase(companyId)}/invoices/note-templates/${templateId}/`, { text })
    .then((response: AxiosResponse) => response.data);

export const deleteInvoiceNoteTemplate = (companyId: number, templateId: string): Promise<void> =>
  axiosInstance
    .delete(`${invoicingBase(companyId)}/invoices/note-templates/${templateId}/`)
    .then(() => undefined);

export type InvoiceCurrencyTotals = Record<string, string>;

// "total" also carries invoicesQuantity — the count of invoices in range
// regardless of currency — alongside the per-currency amounts.
export interface InvoiceGrandTotal {
  invoicesQuantity: number;
  [currencyCode: string]: string | number;
}

// Status/currency keys come back lowercase ("sent", "usd") on purpose — the
// axios response interceptor runs every payload through humps' camelizeKeys,
// which would otherwise mangle all-caps keys like "SENT"/"USD" into "sENT"/"uSD".
export interface InvoiceSummary {
  total: InvoiceGrandTotal;
  draft: InvoiceCurrencyTotals;
  sent: InvoiceCurrencyTotals;
  overdue: InvoiceCurrencyTotals;
  paid: InvoiceCurrencyTotals;
  voided: InvoiceCurrencyTotals;
}

export const getInvoiceSummary = (
  companyId: number,
  startDate: string,
  endDate: string
): Promise<InvoiceSummary> =>
  axiosInstance
    .get(`${invoicingBase(companyId)}/invoices/summary/`, {
      params: { startDate, endDate },
    })
    .then((response: AxiosResponse) => response.data.summary);

export const getUnreconciledDeposits = (
  companyId: number,
  accountId: number,
  params: { startDate: string; currency: string }
): Promise<UnreconciledDepositsResponse> =>
  axiosInstance
    .get(`${invoicingBase(companyId)}/${accountId}/deposits/unreconciled/`, { params })
    .then((response: AxiosResponse) => response.data);

export const createDepositReconciliations = (
  companyId: number,
  accountId: number,
  payload: CreateDepositReconciliationsPayload
): Promise<DepositReconciliation[]> =>
  axiosInstance
    .post(`${invoicingBase(companyId)}/${accountId}/deposits/reconcile/`, payload)
    .then((response: AxiosResponse) => response.data);

export const createManualPaymentReconciliation = (
  companyId: number,
  payload: CreateManualPaymentReconciliationPayload
): Promise<ManualPaymentReconciliation> =>
  axiosInstance
    .post(`${invoicingBase(companyId)}/manual_payment_reconciliations/`, payload)
    .then((response: AxiosResponse) => response.data);
